import React from 'react';

interface ReferenceRangeToggleProps {
  source: 'lab' | 'standard';
  onChange: (source: 'lab' | 'standard') => void;
  hasStandardRanges: boolean;
  showReferenceRanges: boolean;
  onShowReferenceRangesChange: (show: boolean) => void;
}

export const ReferenceRangeToggle: React.FC<ReferenceRangeToggleProps> = ({
  source,
  onChange,
  hasStandardRanges,
  showReferenceRanges,
  onShowReferenceRangesChange
}) => {
  return (
    <div className="bg-white rounded-lg shadow-sm p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-900">Reference Ranges</h3>
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={showReferenceRanges}
            onChange={(e) => onShowReferenceRangesChange(e.target.checked)}
            className="sr-only"
          />
          <div className={`relative w-11 h-6 rounded-full transition-colors ${
            showReferenceRanges ? 'bg-blue-600' : 'bg-gray-300'
          }`}>
            <div className={`absolute top-0.5 left-0.5 bg-white w-5 h-5 rounded-full transition-transform ${
              showReferenceRanges ? 'transform translate-x-5' : ''
            }`}></div>
          </div>
          <span className="ml-2 text-sm text-gray-700">
            {showReferenceRanges ? 'Show' : 'Hide'}
          </span>
        </label>
      </div>
      
      <div className="space-y-2">
        <label className="flex items-center">
          <input
            type="radio"
            name="rangeSource"
            value="lab"
            checked={source === 'lab'}
            onChange={(e) => onChange('lab')}
            className="mr-2 text-blue-600 focus:ring-blue-500"
          />
          <div>
            <span className="text-sm font-medium text-gray-900">Lab Ranges</span>
            <p className="text-xs text-gray-500">Use reference ranges from your lab reports</p>
          </div>
        </label>
        
        <label className="flex items-center">
          <input
            type="radio"
            name="rangeSource"
            value="standard"
            checked={source === 'standard'}
            onChange={(e) => onChange('standard')}
            disabled={!hasStandardRanges}
            className="mr-2 text-blue-600 focus:ring-blue-500"
          />
          <div>
            <span className={`text-sm font-medium ${hasStandardRanges ? 'text-gray-900' : 'text-gray-400'}`}>
              Standard Ranges
            </span>
            <p className="text-xs text-gray-500">
              {hasStandardRanges 
                ? 'Use standardized medical reference ranges'
                : 'Not available for all tests'
              }
            </p>
          </div>
        </label>
      </div>
      
      {source === 'standard' && (
        <div className="mt-3 p-3 bg-blue-50 rounded-md">
          <p className="text-xs text-blue-800">
            <span className="font-medium">Note:</span> Standard ranges are based on Mayo Clinic, 
            LabCorp, and medical literature. They may differ from your lab's ranges.
          </p>
        </div>
      )}
    </div>
  );
};